import { useStore } from '@nanostores/react';
import {
  announceAttemptTransition,
  announceAttemptHydraTransition,
  childAppLoadedStore,
  setActiveRoute,
  setNavCollapsed,
  subscribeToTransitionConfirmation,
  subscribeToHydraTransitionConfirmation,
} from '@customerio/hydra-nav-state';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setAppContainerFadedOut, setAppContainerOutletRendered } from '@/stores/root';
import { getAppBundleFromUrl } from '@/utils/navigation';
import type { AppBundle } from '@/utils/app-bundles';
import { useActiveBundle } from '../../context/active-bundle';

export interface TargetTransitionOptions {
  shouldFadeOut?: boolean;
  shouldUnrenderOutlet?: boolean;
  shouldReplaceHistoryEntry?: boolean;
  targetApp?: AppBundle;
}

export interface TargetTransition {
  url: string;
  options?: TargetTransitionOptions;
}

async function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const SafeNavigateContext = createContext<((url: string, opts?: TargetTransitionOptions) => void) | null>(null);

function _useSafeNavigate() {
  const navigate = useNavigate();
  const childAppLoaded = useStore(childAppLoadedStore);
  const activeApp = useActiveBundle();

  // Use refs and update them with effects so that we can reference the current
  // values in the below callbacks without having to add them to the dependency
  // arrays.
  const childAppLoadedRef = useRef(childAppLoaded);
  useEffect(() => {
    childAppLoadedRef.current = childAppLoaded;
  }, [childAppLoaded]);
  const activeAppRef = useRef(activeApp);
  useEffect(() => {
    activeAppRef.current = activeApp;
  }, [activeApp]);

  const unsubscribeFromTransitionConfirmation = useRef<(() => void) | null>(null);

  const nextTransition = useRef<TargetTransition | null>(null);
  const targetTransition = useRef<TargetTransition | null>(null);

  const [hasNavigated, setHasNavigated] = useState(true);

  // If there is a stored transition once the child app has loaded, perform the
  // transition.
  // A stored transition can occur when a user clicks a link while the child app
  // is still loading.
  useEffect(() => {
    if (nextTransition.current != null && childAppLoaded) {
      const transition = nextTransition.current;
      nextTransition.current = null;

      performNavigate(transition);
    }

    if (childAppLoaded) {
      nextTransition.current = null;
    }
  }, [childAppLoaded]);

  // Clean up when components unmount
  useEffect(() => {
    return () => {
      nextTransition.current = null;
      targetTransition.current = null;
      unsubscribeFromTransitionConfirmation.current?.();
    };
  }, []);

  useEffect(() => {
    if (hasNavigated) {
      return;
    }

    const finishNavigation = async () => {
      const transition = targetTransition.current;
      if (transition != null) {
        setActiveRoute(transition.url);

        targetTransition.current = null;
        if (transition.options?.shouldFadeOut ?? false) {
          setAppContainerFadedOut(false);
        }
        if (transition.options?.shouldUnrenderOutlet ?? false) {
          setAppContainerOutletRendered(true);
        }

        setHasNavigated(true);
      }
    };

    finishNavigation();
  }, [hasNavigated]);

  const performNavigate = useCallback(async (transition: TargetTransition | null) => {
    unsubscribeFromTransitionConfirmation.current?.();

    if (transition != null) {
      setNavCollapsed(false);
      if (transition.options?.shouldFadeOut ?? false) {
        setAppContainerFadedOut(true);
        // Wait for animation to finish
        await timeout(250);
      }
      if (transition.options?.shouldUnrenderOutlet ?? false) {
        // A timeout of 0 makes sure the outlet is destroyed before continuing
        // with the transition
        await timeout(0);
        setAppContainerOutletRendered(false);
      }

      // Only call "navigate" if:
      // - we're unrendering the main outlet - which usually means we're
      //   navigating to a different app.
      //   If we're not unrendering the outlet, the child app is responsible
      //   for all "navigate" events and has control over the browser's history
      //   (so that we don't create duplicate history entries).
      // or
      // - there is no active app - which means there is no embedded app and
      //   we're navigating between Hydra UI routes
      if ((transition.options?.shouldUnrenderOutlet ?? false) || activeAppRef.current == null) {
        navigate(transition.url, {
          replace: transition.options?.shouldReplaceHistoryEntry ?? false,
        });
      }

      // Let the effect above handle the rest of the transition. This ensures
      // that the activeRoute is set, and the app container faded in, only after
      // the navigation has completed.
      targetTransition.current = transition;
      setHasNavigated(false);
    }
  }, []);

  const safeNavigate = useCallback((url: string, opts?: TargetTransitionOptions) => {
    // Reset navigation states on each click - there can only be one active
    // navigation attempt at a time.
    setHasNavigated(true);
    nextTransition.current = null;
    targetTransition.current = null;

    const isDifferentApp =
      opts?.targetApp != null
        ? opts.targetApp !== activeAppRef.current
        : getAppBundleFromUrl(url) !== activeAppRef.current;
    const shouldUnrenderOutlet = (opts?.shouldUnrenderOutlet ?? false) || url === '/logout' || isDifferentApp;

    const transition = {
      url,
      options: {
        shouldFadeOut: opts?.shouldFadeOut ?? false,
        shouldUnrenderOutlet,
        shouldReplaceHistoryEntry: opts?.shouldReplaceHistoryEntry ?? false,
      },
    };
    if (activeAppRef.current != null && !childAppLoadedRef.current) {
      nextTransition.current = transition;
    } else if (activeAppRef.current == null) {
      // Navigate for hydra-only pages
      unsubscribeFromTransitionConfirmation.current?.();
      unsubscribeFromTransitionConfirmation.current = subscribeToHydraTransitionConfirmation(() =>
        performNavigate(transition),
      );

      announceAttemptHydraTransition();
    } else {
      unsubscribeFromTransitionConfirmation.current?.();
      unsubscribeFromTransitionConfirmation.current = subscribeToTransitionConfirmation(() =>
        performNavigate(transition),
      );
      announceAttemptTransition();
    }
  }, []);

  return safeNavigate;
}

export function useSafeNavigate() {
  const safeNavigate = useContext(SafeNavigateContext);

  if (safeNavigate == null) {
    throw new Error('useSafeNavigate must be used within a SafeNavigateProvider');
  }

  return safeNavigate;
}

export function SafeNavigateProvider({ children }: { children: React.ReactNode }) {
  const safeNavigate = _useSafeNavigate();

  return <SafeNavigateContext.Provider value={safeNavigate}>{children}</SafeNavigateContext.Provider>;
}
